// ======================================================================
// File: src/articles/StudyMotivation.jsx
// Article: How to Stay Motivated to Study – Proven Techniques
// ======================================================================

import { Typography, Divider } from "@mui/material";


export default function StudyMotivation() {
  return (
    <>
      {/* INTRO */}
      <Typography sx={{ mb: 2 }}>
        Staying motivated to study is one of the biggest challenges students
        face. Everyone wants to pass exams, get high grades, and succeed, but
        motivation often disappears after a few days or when topics become
        difficult. The truth is: motivation does not come by magic — it must be
        built and maintained deliberately.
      </Typography>

      <Typography sx={{ mb: 3 }}>
        This guide explains practical, psychology-backed strategies that will
        help you stay focused, energized, and consistent in your study routine.
        Whether you’re preparing for WAEC, NECO, or JAMB, these techniques will
        help you study with more discipline and less stress.
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* SECTION 1 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        1. Understand Your “Why”
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Motivation becomes stronger when you know exactly **why** you are
        studying. Ask yourself:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>What future do I want?</li>
        <li>What course do I dream of studying?</li>
        <li>What kind of life do I want in 5–10 years?</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        When your vision is clear, your study sessions become more meaningful.
        You stop reading because someone forced you — you read because **your
        future depends on it**.
      </Typography>

      {/* SECTION 2 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        2. Set Small, Clear, Achievable Goals
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Big goals create fear. Small goals create progress.
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Instead of “study Chemistry today,” say “Finish two topics.”</li>
        <li>Instead of “study for 8 hours,” say “Do 25-minute sessions.”</li>
        <li>
          Instead of “cover the whole Maths syllabus,” say “Master algebra this week.”
        </li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        Every time you achieve a small goal, you feel progress — and progress is
        the strongest source of motivation.
      </Typography>

      {/* SECTION 3 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        3. Use the 25-Minute Focus Rule (Pomodoro Technique)
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Long study hours without breaks lead to burnout and boredom. Instead,
        study like this:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Work for 25 minutes</li>
        <li>Take a 5-minute break</li>
        <li>Repeat four cycles</li>
        <li>Take a 15-minute longer break</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        This method increases concentration, reduces mental fatigue, and makes
        studying feel easier and less stressful.
      </Typography>

      {/* SECTION 4 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        4. Remove Distractions (Especially Your Phone)
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Social media notifications are the biggest killers of study motivation.
        During study time:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Turn off your data</li>
        <li>Put your phone in another room</li>
        <li>Use airplane mode</li>
        <li>Use apps that block distractions</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        When distractions disappear, study sessions become smoother and you
        naturally become more focused.
      </Typography>

      {/* SECTION 5 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        5. Study in a Clean and Organized Environment
      </Typography>

      <Typography sx={{ mt: 2 }}>
        A messy environment = a distracted mind.  
        A clean space = clearer thinking.
      </Typography>

      <Typography sx={{ mb: 3 }}>
        Always study on a clean desk, good lighting, and proper ventilation. A
        calm environment increases motivation and reduces stress.
      </Typography>

      {/* SECTION 6 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        6. Reward Yourself After Studying
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Your brain loves rewards. After completing a study goal:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Eat your favorite snack</li>
        <li>Watch one episode</li>
        <li>Play a game for 10 minutes</li>
        <li>Take a short walk</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        When your brain learns that “studying = reward,” your motivation grows
        automatically.
      </Typography>

      {/* SECTION 7 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        7. Use Study Groups or Accountability Partners
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Studying with others increases motivation because it gives you:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>Competition</li>
        <li>Support</li>
        <li>Shared goals</li>
        <li>Explanation of difficult concepts</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        Even studying with one partner once a week can double your consistency.
      </Typography>

      {/* SECTION 8 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        8. Track Your Progress Weekly
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Motivation grows when you see results. Every Sunday, ask yourself:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>What topics did I finish?</li>
        <li>What topics are left?</li>
        <li>What mistakes did I repeat?</li>
        <li>How can I improve next week?</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        Progress tracking prevents discouragement and keeps you disciplined.
      </Typography>

      {/* SECTION 9 */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 3 }}>
        9. Remove Negative Self-Talk
      </Typography>

      <Typography sx={{ mt: 2 }}>
        Statements like:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>“I’m not good at Maths”</li>
        <li>“I will fail”</li>
        <li>“Others are smarter than me”</li>
      </Typography>

      <Typography sx={{ mb: 3 }}>
        …destroys motivation. Replace them with:
      </Typography>

      <Typography component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>“I can improve with practice.”</li>
        <li>“Every day I’m getting better.”</li>
        <li>“I am capable of passing this exam.”</li>
      </Typography>

      {/* ENDING */}
      <Divider sx={{ my: 4 }} />

      <Typography sx={{ mb: 2 }}>
        Motivation is not something you wait for — it is something you build. If
        you understand your goals, remove distractions, use smart study
        techniques, and stay consistent, you will achieve outstanding results in
        any exam.
      </Typography>

      <Typography>
        Stay focused, stay consistent, and keep moving — your success is closer
        than you think.
      </Typography>
	  
	 
    </>
  );
}
